/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ContentType } from '../ContentType';
import { ViewCard } from '../../components/CardView/ContentCard/ViewCard';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { IContentDefinition } from './IContentDefinition';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import { GetViewsResult } from '../httpClient/MetaDataResults';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { View } from '../SearchResultTypes';
import { SessionManagementService } from '../../services/SessionManagementService';
import { UserMapper } from '../../../common/utils/UserMapper';
import { IUser } from '../IUser';
import { SortBy } from '../SortSelection';
import { LocationTypes } from '../ILocationInfo';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { DataGrid } from '@tableau/widgets-datagrid';
import { LinkCell, ILink } from '../../components/listView/dataGridCells/LinkCell';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const viewContentDefinition: IContentDefinition<View, GetViewsResult> = {
  getSectionTitle: () => Messages.ViewSectionTitle(),
  getUrl: (urls) => urls.searchViewsUrl(),
  getPath: (urls) => urls.searchViewsPath(),
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      const owner = crassDataItem.ownerId
        ? UserMapper.mapOne({
          id: crassDataItem.ownerId.toString(),
          displayName: crassDataItem.ownerName,
          domainName: crassDataItem.ownerDomain || '',
          username: crassDataItem.ownerAlias || '',
        } as IUser)
        : UserMapper.mapOne(undefined);
      return {
        contentType: ContentType.view,
        name: crassDataItem.title,
        id: crassDataItem.id.toString(),
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).redirectToViewUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
        crassData: crassDataItem,
        owner: owner,
        sheetNumber: crassDataItem.sheetNumber,
        location: crassDataItem.locationName
          ? {
            name: crassDataItem.locationName,
            id: crassDataItem.projectId?.toString(),
            type: crassDataItem.containerType ? crassDataItem.containerType as LocationTypes : undefined,
          } : undefined,
        workbook: crassDataItem.workbookId ? { id: crassDataItem.workbookId.toString(), name: crassDataItem.containerName, owner: owner } : undefined,
      };
    }) as View[];
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metaDataViews: { [viewsId: string]: { thumbnail: string; workbookId: string; } } = {};
    const favoriteMetaData: { [viewsId: string]: boolean } = {};
    if (metadata.total > 0) {
      metadata.items.views.forEach((item) => {
        metaDataViews[item.id] = { thumbnail: '', workbookId: '' };
        metaDataViews[item.id].thumbnail = item.thumbnailUrl;
        metaDataViews[item.id].workbookId = item.workbookId;
      });
      metadata.items.favorites.forEach((id) => {
        favoriteMetaData[id] = true;
      });
    }

    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      return {
        ...searchResultItem,
        thumbnailUrl: metaDataViews[crassDataItem.id.toString()] ? metaDataViews[crassDataItem.id.toString()].thumbnail : '',
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).redirectToViewUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
        tags: crassDataItem.tags || [],
        hitsTotal: crassDataItem.hitsTotal,
        project: { name: crassDataItem.projectName || '', id: crassDataItem.projectId?.toString() || '' },
        favorite: favoriteMetaData[crassDataItem.id.toString()] ? favoriteMetaData[crassDataItem.id.toString()] : false,
      };
    });
  },
  defaultDisplayMode: 'thumbnail',
  listViewColumns: [
    CommonColumns.name,
    CommonColumns.owner,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'sheetNumber',
        dataKey: 'sheetNumber',
        sortable: false,
        headerText: Messages.column_sheetNumber(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('sheetNumber'),
      }
    },
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'workbook',
        dataKey: 'workbook',
        sortable: false,
        headerText: Messages.column_workbook(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LinkCell((row: DataGrid.Row): ILink => {
          return (
            row.data.workbook && {
              text: row.data.workbook.name,
              url: parameters.listViewParams.urls.workbookUrl(row.data.workbook.id),
            }
          )
        }),
        ...{ growToFit: true },
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'hitsTotal',
        dataKey: 'hitsTotal',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_hitsTotal(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: View) => data.hitsTotal),
        maxColumnWidth: 10,
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'modified',
        dataKey: 'modifiedTime',
        sortable: false,
        headerText: Messages.column_updated(),
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('modifiedTime'),
        ...{ growToFit: true },
      };
    },
  ],
  cardComponent: ViewCard,
  maxItemCountInMixedContentTypeResultPage: 8,
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.redirectToViewUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter, ConnectedTagFilter],
  getMetaDataRequests: GetMetaDataRequests.GetViewsRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getPopularitySortSelection()],
  shouldBeAvailable: () => true,
  favoritable: () => true,
};
